<script>
  import { onMount } from 'svelte';
  import { Link } from 'svelte-routing';
  import PromotionCard from '../components/PromotionCard.svelte';
  import { getApiUrl } from '../api';
  import { authStore } from '../authStore';
  import { selectedCountry } from '../countryStore';
  import { t } from '../i18nStore';
  import IntroCarousel from '../components/IntroCarousel.svelte';
  import MealDeals from '../components/MealDeals.svelte';
  import Banners from "../components/Banners.svelte";
  import AIRecommendation from '../components/AIRecommendation.svelte';
  import TaxEnvelopeFab from '../components/TaxEnvelopeFab.svelte';

  let promotions = [];
  let isLoading = false;
  let error = '';
  let sortBy = 'newest'; 
  
  let page = 1;
  const pageSize = 10;
  let hasMore = false;
  let currentUser = null;

    // Subscribe to authStore
  authStore.subscribe(value => {
    currentUser = value.user;
  });

  $: {
    fetchActivePromotions(sortBy, $selectedCountry, true);
  }

  async function fetchActivePromotions(currentSortBy, currentCountry, reset = false) {
      if (reset) { page = 1; promotions = []; hasMore = false; }
      isLoading = true; error = '';
      try {
        const localDate = new Date();
        const currentDate = `${localDate.getFullYear()}-${String(localDate.getMonth() + 1).padStart(2, '0')}-${String(localDate.getDate()).padStart(2, '0')}`;
        const url = getApiUrl(`/promotions/active?sortBy=${currentSortBy}&countryCode=${currentCountry}&page=${page}&pageSize=${pageSize}&currentDate=${currentDate}`);
        const response = await fetch(url);
        if (!response.ok) throw new Error('Could not fetch active promotions.');
        const result = await response.json();
        const newPromos = result.data || [];
        if (reset) promotions = newPromos;
        else promotions = [...promotions, ...newPromos];
        hasMore = page < result.totalPages;
      } catch (e) { error = e.message; } finally { isLoading = false; }
  }

  function loadMore() { page += 1; fetchActivePromotions(sortBy, $selectedCountry, false); }
</script>

<!-- Add Carousel here at the very top -->
<IntroCarousel />
<AIRecommendation />
<MealDeals />
<Banners />

<div class="page-container">

  <div class="header-controls">
    <h2>{$t('home.active_promotions')} ({$selectedCountry})</h2>
    <div class="sort-control">
      <label for="sort-by">{$t('home.sort_by')}:</label>
      <select id="sort-by" bind:value={sortBy}>
        <option value="newest">{$t('home.sort.newest')}</option>
        <option value="ending_soon">{$t('home.sort.ending_soon')}</option>
        <option value="name">{$t('home.sort.name')}</option>
        <option value="reward_desc">{$t('home.sort.reward')}</option>
        <option value="spending_asc">{$t('home.sort.spend')}</option>
      </select>
    </div>
  </div>
  
  {#if error} <p class="error">{error}</p> {/if}

  {#if promotions.length === 0 && !isLoading}
    <p>{$t('home.no_data')}</p>
  {:else}
    <div class="promo-list">
      {#each promotions as promo (promo._id)}
        <PromotionCard {promo} showStartDate={false} />
      {/each}
    </div>
  {/if}
  
  {#if isLoading} <p class="loading">{$t('home.loading')}</p> {/if}

  {#if hasMore && !isLoading}
      <div class="load-more-container">
        <button class="load-more-btn" on:click={loadMore}>{$t('home.load_more')}</button>
      </div>
  {/if}
</div>

<!-- Tax Rebate Floating Envelope FAB -->
<TaxEnvelopeFab />

<!-- FABs -->
<div class="fab-container">
  <Link to="/search" class="fab-btn secondary-fab" title="Search Promotions">
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>
  </Link>
  <!-- Protected Add/Scan FAB -->
  {#if currentUser}
    <Link to="/scan" class="fab-btn primary-fab" title="Scan Promotion">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
    </Link>
   {/if}
</div>

<style>
  .page-container {
    max-width: 1200px;
    margin: 0 auto 2rem;
    padding: 0;
  }
  
  .header-controls, .promo-list, p, .load-more-container { padding: 0 1rem; }
  
  .header-controls { display: flex; justify-content: space-between; align-items: center; margin-bottom: 2rem; flex-wrap: wrap; gap: 1rem; }
  .sort-control { display: flex; align-items: center; gap: 0.5rem; }
  select { padding: 0.5rem; border-radius: 4px; border: 1px solid #ccc; font-size: 1rem; }
  .promo-list { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
  @media (min-width: 600px) { .promo-list { grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); } }
  .error { color: red; font-weight: bold; text-align: center; }
  .loading { text-align: center; margin-top: 2rem; color: #666; }
  .load-more-container { text-align: center; margin-top: 2rem; }
  .load-more-btn { background-color: #f8f9fa; color: #333; border: 1px solid #ccc; padding: 0.75rem 2rem; font-size: 1rem; cursor: pointer; transition: background 0.2s; border-radius: 4px; }
  .load-more-btn:hover { background-color: #e2e6ea; }
  .fab-container { position: fixed; bottom: 2rem; right: 2rem; display: flex; flex-direction: column; gap: 1rem; align-items: center; z-index: 1000; }
  :global(.fab-btn) { display: flex; justify-content: center; align-items: center; border-radius: 50%; box-shadow: 0 4px 12px rgba(0,0,0,0.2); color: white; text-decoration: none; transition: transform 0.2s, box-shadow 0.2s; }
  :global(.fab-btn:hover) { transform: scale(1.05); box-shadow: 0 6px 16px rgba(0,0,0,0.3); }
  :global(.primary-fab) { width: 64px; height: 64px; background-color: #007bff; }
  :global(.secondary-fab) { width: 56px; height: 56px; background-color: #6c757d; }
</style>